/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import { ALL_FILTER_VALUE, sanitizeNameList } from "../../../utils/perqReportDrilldown";
import {
  fromSavedDate,
  savedOptionId,
  savedOptionList,
  toSavedDate,
  toSavedOption,
  toSavedOptions,
} from "../../../components/saved_queries/savedFilterHelpers";
import { ALL_ORDER_TYPE, DATE_FILTERS, ORDER_TYPES, defaultBillingReportFilters } from "./constants";

const CUSTOM_DATE = "Custom Date";
const noLabel = () => null;

const quotedList = (names) => {
  const quoted = names.map((name) => `'${name}'`);
  return quoted.length === 1 ? quoted[0] : `${quoted.slice(0, -1).join(", ")} and ${quoted.at(-1)}`;
};

export const unavailablePerqsMessage = (names) =>
  names.length === 1
    ? `Applied perq ${quotedList(names)} is no longer available and was removed from the filters.`
    : `Applied perqs ${quotedList(names)} are no longer available and were removed from the filters.`;

/**
 * Billing Report filters <-> saved query JSON.
 * lookups: { restaurantGroupName, restaurantName }, each id => label.
 * deserialize drops the record's `unavailable_discount_names` and returns a notice naming them.
 */
export const billingSavedQueryAdapter = {
  version: 1,
  defaults: defaultBillingReportFilters,

  serialize: (filters, lookups = {}) => {
    const isCustom = filters.dateFilter === CUSTOM_DATE;
    const groupId = filters.restaurant_group_id === ALL_FILTER_VALUE ? null : filters.restaurant_group_id;
    const restaurantIds = (filters.restaurant_ids || []).filter((id) => id !== ALL_FILTER_VALUE);

    return {
      dateFilter: filters.dateFilter,
      customDateFrom: isCustom ? toSavedDate(filters.customDateFrom) : null,
      customDateTo: isCustom ? toSavedDate(filters.customDateTo) : null,
      restaurant_group:
        groupId === null || groupId === undefined
          ? null
          : toSavedOption(groupId, lookups.restaurantGroupName?.(groupId)),
      restaurants: toSavedOptions(restaurantIds, lookups.restaurantName || noLabel),
      order_type: filters.order_type,
      applied_discount_names: sanitizeNameList(filters.applied_discount_names).map((name) =>
        toSavedOption(name, name),
      ),
    };
  },

  deserialize: (saved, _version, record = {}) => {
    const source = saved && typeof saved === "object" ? saved : {};
    const defaults = defaultBillingReportFilters();
    const savedPerqs = sanitizeNameList(savedOptionList(source.applied_discount_names).map((option) => option.id));
    const unavailable = new Set(sanitizeNameList(record.unavailable_discount_names));
    const removedPerqs = savedPerqs.filter((name) => unavailable.has(name));

    const dateFilter = DATE_FILTERS.includes(source.dateFilter) ? source.dateFilter : defaults.dateFilter;
    const isCustom = dateFilter === CUSTOM_DATE;
    const group = savedOptionId(source.restaurant_group) === null ? null : source.restaurant_group;
    const restaurants = savedOptionList(source.restaurants)
      .filter((option) => Number.isInteger(Number(option.id)))
      .map((option) => ({ id: Number(option.id), label: option.label }));

    return {
      filters: {
        dateFilter,
        customDateFrom: isCustom ? fromSavedDate(source.customDateFrom) : defaults.customDateFrom,
        customDateTo: isCustom ? fromSavedDate(source.customDateTo) : defaults.customDateTo,
        restaurant_group_id: group ? group.id : ALL_FILTER_VALUE,
        restaurant_ids: restaurants.length ? restaurants.map((option) => option.id) : [ALL_FILTER_VALUE],
        order_type: ORDER_TYPES.includes(source.order_type) ? source.order_type : ALL_ORDER_TYPE,
        applied_discount_names: savedPerqs.filter((name) => !unavailable.has(name)),
      },
      labelOptions: { restaurant_group: group, restaurants },
      notices: removedPerqs.length ? [unavailablePerqsMessage(removedPerqs)] : [],
    };
  },
};
